/**
 * i18next setup (ARCHITECTURE.md §2, §11.6 — "All user-facing strings go through i18n keys").
 *
 * One default namespace, so keys read as `t('bots.fields.takeProfit.label')`. English is the
 * default and the fallback for any string a translation lacks; Turkish and English are bundled,
 * every other shipped language (src/i18n/languages.ts) is a chunk loaded when it is first used.
 *
 * The language: a public page's language address (`/tr/login`, `PAGE_LOCALES` in seo/site.ts)
 * wins, then an explicit choice saved on this device, then the browser's languages in order, then
 * English. The signed-in user's `locale` from `GET /auth/me` is adopted by
 * `useSyncLocaleFromUser()` when this device has no saved choice, and a choice made here is
 * written back with `PATCH /auth/me`.
 */

import { setDefaultOptions, type Locale as DateFnsLocale } from 'date-fns';
import { enUS, es, ptBR, ru, tr as trLocale, vi } from 'date-fns/locale';
import i18next, { type BackendModule, type i18n as I18nInstance, type ResourceKey } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { initReactI18next, useTranslation } from 'react-i18next';

import { safeStorage } from '@/lib/api';
import { pathLocale } from '@/seo/site';
import { useAuthStore } from '@/stores/authStore';
import type { Locale } from '@/types/api';

import { DEFAULT_LANGUAGE, LANGUAGES, matchLanguage } from './languages';
import en from './locales/en.json';
import tr from './locales/tr.json';

export const DEFAULT_LOCALE: Locale = DEFAULT_LANGUAGE;
export const LOCALE_STORAGE_KEY = 'hopebot.locale';

export const resources = {
  en: { translation: en },
  tr: { translation: tr },
} as const;

/** Every other translation, as a lazily imported chunk keyed by its file path. */
const LAZY_LOCALES = import.meta.glob<{ default: ResourceKey }>([
  './locales/*.json',
  '!./locales/en.json',
  '!./locales/tr.json',
]);

function lazyPath(code: string): string {
  return `./locales/${code}.json`;
}

/** The languages this build ships: the bundled two and every translation file present. */
export const SUPPORTED_LOCALES: readonly Locale[] = LANGUAGES.map((language) => language.code).filter(
  (code) => code in resources || lazyPath(code) in LAZY_LOCALES,
);

/** Narrow any BCP-47 tag (`en-GB`, `pt-PT`, `es-MX`) to a shipped language, else English. */
export function normalizeLocale(value: string | null | undefined): Locale {
  return matchLanguage(value, SUPPORTED_LOCALES) ?? DEFAULT_LOCALE;
}

/** The language the address names (`/tr/…`), or null at a root address. Never saved. */
function addressLocale(): Locale | null {
  return typeof window === 'undefined' ? null : pathLocale(window.location.pathname);
}

/**
 * The language a first visit opens in: the address's language, then the saved choice, then the
 * browser's languages.
 */
export function detectLocale(): Locale {
  const fromAddress = addressLocale();
  if (fromAddress) return fromAddress;
  const saved = matchLanguage(safeStorage.get(LOCALE_STORAGE_KEY), SUPPORTED_LOCALES);
  if (saved) return saved;
  if (typeof navigator !== 'undefined') {
    const tags = [...(navigator.languages ?? []), navigator.language];
    for (const tag of tags) {
      const match = matchLanguage(tag, SUPPORTED_LOCALES);
      if (match) return match;
    }
  }
  return DEFAULT_LOCALE;
}

const DATE_FNS_LOCALES: Record<Locale, DateFnsLocale> = {
  en: enUS,
  tr: trLocale,
  es,
  'pt-BR': ptBR,
  ru,
  vi,
};

/** Keep date-fns' global default in step, so bare `format()` calls are localised too. */
function applySideEffects(locale: Locale): void {
  setDefaultOptions({ locale: DATE_FNS_LOCALES[locale] });
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

/** Loads a lazy translation the first time its language is used. */
const lazyBackend: BackendModule = {
  type: 'backend',
  init() {},
  read(language, _namespace, callback) {
    const load = LAZY_LOCALES[lazyPath(language)];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (module) => callback(null, module.default),
      (error: unknown) => callback(error instanceof Error ? error : new Error(String(error)), false),
    );
  },
};

let initialized = false;

/**
 * Settles once the first language's strings are in. main.tsx waits for it before the first
 * render, so a visitor reading a lazily loaded language never sees a flash of English.
 */
export let i18nReady: Promise<unknown> = Promise.resolve();

export function initI18n(): I18nInstance {
  if (initialized) return i18next;
  initialized = true;

  i18nReady = i18next
    .use(lazyBackend)
    .use(initReactI18next)
    .init({
      resources,
      partialBundledLanguages: true,
      lng: detectLocale(),
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      load: 'currentOnly',
      defaultNS: 'translation',
      ns: ['translation'],
      interpolation: {
        // React already escapes everything it renders.
        escapeValue: false,
      },
      returnNull: false,
      react: {
        useSuspense: false,
      },
    })
    .catch(() => undefined);

  applySideEffects(normalizeLocale(i18next.resolvedLanguage ?? i18next.language));
  i18next.on('languageChanged', (lng: string) => {
    applySideEffects(normalizeLocale(lng));
  });

  return i18next;
}

/** The singleton, initialised on first import. `main.tsx` passes it to `I18nextProvider`. */
export const i18n = initI18n();

/* -------------------------------------------------------------------------- */
/* useLocale                                                                  */
/* -------------------------------------------------------------------------- */

export interface UseLocaleResult {
  locale: Locale;
  supportedLocales: readonly Locale[];
  /** Switch the UI language, persist it locally and (if signed in) to the user profile. */
  setLocale: (next: Locale) => Promise<void>;
  /** True while `PATCH /auth/me` is in flight. The UI has already switched by then. */
  isSaving: boolean;
}

/**
 * The language switcher's hook.
 *
 * The UI language changes immediately and optimistically; persisting it to the profile is a
 * background nicety, and a failure there is swallowed — a dead backend must not stop someone
 * from reading the app in their own language.
 */
export function useLocale(): UseLocaleResult {
  const { i18n: instance } = useTranslation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const updateProfile = useAuthStore((state) => state.updateProfile);
  const [isSaving, setIsSaving] = useState(false);

  const locale = normalizeLocale(instance.resolvedLanguage ?? instance.language);

  const setLocale = useCallback(
    async (next: Locale) => {
      if (next === locale) return;

      await instance.changeLanguage(next);
      applySideEffects(next);
      safeStorage.set(LOCALE_STORAGE_KEY, next);

      if (!isAuthenticated) return;

      setIsSaving(true);
      try {
        await updateProfile({ locale: next });
      } catch {
        /* the local preference still stands */
      } finally {
        setIsSaving(false);
      }
    },
    [instance, isAuthenticated, locale, updateProfile],
  );

  return { locale, supportedLocales: SUPPORTED_LOCALES, setLocale, isSaving };
}

/**
 * Adopt the server-side `user.locale` once, at bootstrap — but only when the visitor has never
 * made an explicit choice on this device, so a manual switch is never overwritten on reload.
 * Mount this once from `App.tsx`.
 */
export function useSyncLocaleFromUser(): void {
  const userLocale = useAuthStore((state) => state.user?.locale);

  useEffect(() => {
    if (!userLocale) return;
    if (safeStorage.get(LOCALE_STORAGE_KEY)) return;
    // Opened at a language address (`/tr/…`): that language stands, as it does over a saved one.
    if (addressLocale()) return;
    const next = normalizeLocale(userLocale);
    if (next === normalizeLocale(i18next.resolvedLanguage ?? i18next.language)) return;
    void i18next.changeLanguage(next);
  }, [userLocale]);
}

export default i18n;
